import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Notification } from '@clarity/contract';
import { useI18n, type Locale } from '../lib/i18n.js';
import { useMarkAllNotificationsRead, useNotifications } from '../queries/index.js';
import { IconBell } from './icons.js';

/**
 * The topbar notification bell (clarity-smart-notifications Req 9.2). Shows an
 * unread badge (polled) and a dropdown of recent notifications. Owner-facing;
 * rendered in the Shell topbar above the per-route ScopeProvider, so it reads the
 * client-wide inbox (`scope: all`). Clicking an item routes to the full
 * Notifications surface.
 */
export function NotificationBell() {
  const { t, locale } = useI18n();
  const { clientId } = useParams();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inbox = useNotifications('all');
  const markAll = useMarkAllNotificationsRead();

  const body = inbox.data?.status === 200 ? inbox.data.body : null;
  const unread = body?.unreadCount ?? 0;
  const recent = (body?.notifications ?? []).slice(0, 6);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  // No client context (admin top level) ⇒ no owner inbox.
  if (!clientId) return null;

  const allHref = `/c/${clientId}/all/notifications`;

  return (
    <div className="notif-bell" ref={ref}>
      <button
        type="button"
        className="ghost notif-bell__btn"
        aria-label={t('notif.open')}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <IconBell />
        {unread > 0 && (
          <span className="notif-bell__dot" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-bell__panel" role="menu">
          <div className="notif-bell__head">
            <strong>{t('notif.title')}</strong>
            {unread > 0 && (
              <button
                type="button"
                className="notif-bell__markall"
                onClick={() => markAll.mutate({ body: {} })}
              >
                {t('notif.markAllRead')}
              </button>
            )}
          </div>
          <div className="notif-bell__list">
            {recent.length === 0 ? (
              <p className="muted notif-bell__empty">{t('notif.empty.title')}</p>
            ) : (
              recent.map((n) => (
                <BellItem
                  key={n.id}
                  n={n}
                  locale={locale as Locale}
                  to={allHref}
                  onOpen={() => setOpen(false)}
                />
              ))
            )}
          </div>
          <div className="notif-bell__foot">
            <Link to={allHref} onClick={() => setOpen(false)}>
              {t('notif.viewAll')} →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function BellItem({
  n,
  locale,
  to,
  onOpen,
}: {
  n: Notification;
  locale: Locale;
  to: string;
  onOpen: () => void;
}) {
  const unread = n.readAt == null;
  return (
    <Link
      to={to}
      onClick={onOpen}
      className={`notif-bell__item notif-bell__item--${n.severity}`}
      data-unread={unread}
    >
      <span className={`notif-bell__ico notif-bell__ico--${n.severity}`} aria-hidden="true" />
      <div className="notif-bell__item-body">
        <p className="notif-bell__item-title">{n.title[locale] ?? n.title.en}</p>
        <p className="notif-bell__item-txt">{n.body[locale] ?? n.body.en}</p>
      </div>
    </Link>
  );
}
